import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router';
import { Language, UpdateParty, type Party, type PartyProfile } from '@syndiqia/registry/contracts';
import { errorMessage, useApi } from '../api';
import { useMembers } from '../comms-queries';
import { Alert, Empty, Field, Loading, PageHeader, validate, type FieldErrors } from '../components/ui';
import { useI18n } from '../i18n';
import { useSyndicate } from '../session';
import { AccessCell, useAccess } from './PartiesPage';
import { OfficerTerms } from './governance/Board';

interface RoleChange {
  id: string;
  userId: string;
  name: string | null;
  role: 'director' | 'manager';
  granted: boolean;
  note: string;
  changedAt: string;
  changedBy: string | null;
}

/** A person, as the board sees them: details, units owned and occupied, access and roles. */
export function MemberPage() {
  const { t } = useI18n();
  const s = useSyndicate();
  const api = useApi();
  const m = t.members;
  const { partyId = '' } = useParams();
  const profile = useQuery({ queryKey: [s.tenantId, 'parties', 'profile', partyId], queryFn: () => api<PartyProfile>(`${s.api}/registry/parties/${partyId}/profile`) });

  if (!s.can('registry.contacts.read')) return <Alert tone="info">{t.parties.boardOnly}</Alert>;
  if (profile.isPending) return <Loading label={t.loading} />;
  if (profile.isError) return <Alert>{errorMessage(profile.error, t)}</Alert>;
  const p = profile.data;
  return (
    <>
      <Link to={`${s.base}/parties`} className="back">
        ← {m.back}
      </Link>
      <PageHeader title={p.party.displayName} />
      <div className="member-grid">
        <Details party={p.party} />
        <Units profile={p} />
        <AccessAndRoles profile={p} />
      </div>
    </>
  );
}

function Details({ party }: { party: Party }) {
  const { t } = useI18n();
  const s = useSyndicate();
  const m = t.members;
  const [editing, setEditing] = useState(false);
  const [values, setValues] = useState({ displayName: party.displayName, email: party.email ?? '', phone: party.phone ?? '', preferredLanguage: party.preferredLanguage, mailingAddress: party.mailingAddress ?? '' });
  const [errors, setErrors] = useState<FieldErrors>({});
  const save = useMemberMutation<UpdateParty, Party>((api, base, input) => api(`${base}/registry/parties/${party.id}`, { method: 'PUT', body: input }));
  const set = (key: keyof typeof values) => (e: { target: { value: string } }) => setValues((v) => ({ ...v, [key]: e.target.value }));
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const result = validate(UpdateParty, values);
    setErrors(result.errors ?? {});
    if (result.data) save.mutate(result.data, { onSuccess: () => setEditing(false) });
  };

  if (!editing) {
    return (
      <section className="panel">
        <header className="dash-card-header">
          <h2>{m.details}</h2>
          {s.can('registry.contacts.write') && (
            <button type="button" className="link-button small" onClick={() => setEditing(true)}>
              {m.edit}
            </button>
          )}
        </header>
        <dl className="facts">
          <dt>{m.email}</dt>
          <dd>{party.email ? <a href={`mailto:${party.email}`}>{party.email}</a> : '—'}</dd>
          <dt>{m.phone}</dt>
          <dd>{party.phone ?? '—'}</dd>
          <dt>{m.language}</dt>
          <dd>{t.parties.languages[party.preferredLanguage]}</dd>
          <dt>{m.mailingAddress}</dt>
          <dd>{party.mailingAddress ?? '—'}</dd>
        </dl>
        {save.isSuccess && <p className="muted small">{m.saved}</p>}
      </section>
    );
  }
  return (
    <form className="panel form" onSubmit={submit} noValidate>
      <h2>{m.details}</h2>
      <Field label={m.name} error={errors.displayName}>
        <input value={values.displayName} onChange={set('displayName')} />
      </Field>
      <ContactFields values={values} set={set} errors={errors} />
      {save.isError && <Alert>{errorMessage(save.error, t)}</Alert>}
      <div className="form-actions">
        <button type="submit" className="button button-primary" disabled={save.isPending}>
          {t.common.save}
        </button>
        <button type="button" className="button" onClick={() => setEditing(false)}>
          {t.common.cancel}
        </button>
      </div>
    </form>
  );
}

/** Email, phone, language and mailing address (shared with "My profile"). */
export function ContactFields({
  values,
  set,
  errors,
}: {
  values: { email: string; phone: string; preferredLanguage: string; mailingAddress: string };
  set: (key: 'email' | 'phone' | 'preferredLanguage' | 'mailingAddress') => (e: { target: { value: string } }) => void;
  errors: FieldErrors;
}) {
  const { t } = useI18n();
  const m = t.members;
  return (
    <>
      <div className="row">
        <Field label={m.email} hint={t.common.optional} error={errors.email}>
          <input type="email" value={values.email} onChange={set('email')} />
        </Field>
        <Field label={m.phone} hint={t.common.optional} error={errors.phone}>
          <input type="tel" value={values.phone} onChange={set('phone')} />
        </Field>
        <Field label={m.language}>
          <select value={values.preferredLanguage} onChange={set('preferredLanguage')}>
            {Language.options.map((l) => (
              <option key={l} value={l}>
                {t.parties.languages[l]}
              </option>
            ))}
          </select>
        </Field>
      </div>
      <Field label={m.mailingAddress} hint={t.common.optional} error={errors.mailingAddress}>
        <input value={values.mailingAddress} onChange={set('mailingAddress')} />
      </Field>
      <p className="muted small">{m.contactNote}</p>
    </>
  );
}

function Units({ profile: p }: { profile: PartyProfile }) {
  const { t, date } = useI18n();
  const s = useSyndicate();
  const m = t.members;
  const when = (from: string, to: string | null) => (to ? m.period(date(from), date(to)) : m.since(date(from)));
  const none = p.ownership.length === 0 && p.occupancy.length === 0;
  return (
    <section className="panel">
      <h2>{m.units}</h2>
      {none && <Empty>{m.noUnits}</Empty>}
      <ul className="unit-history">
        {p.ownership.map((o) => (
          <li key={`${o.unitId}-${o.from}`} className={o.to ? 'past' : undefined}>
            <Link to={`${s.base}/units/${o.unitId}`}>{o.unitLabel}</Link> <span className="badge">{m.owned}</span>
            <span className="muted small">
              {m.share(o.share.numerator, o.share.denominator)} {when(o.from, o.to)}
            </span>
          </li>
        ))}
        {p.occupancy.map((o) => (
          <li key={o.id} className={o.to ? 'past' : undefined}>
            <Link to={`${s.base}/units/${o.unitId}`}>{o.unitLabel}</Link> <span className="badge badge-draft">{m.occupied}</span>
            <span className="muted small">{when(o.from, o.to)}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

function AccessAndRoles({ profile: p }: { profile: PartyProfile }) {
  const { t, date } = useI18n();
  const s = useSyndicate();
  const api = useApi();
  const m = t.members;
  const access = useAccess();
  const members = useMembers();
  const history = useQuery({ queryKey: [s.tenantId, 'members', 'role-history'], queryFn: () => api<RoleChange[]>(`${s.api}/members/role-history`) });
  const [note, setNote] = useState('');
  const change = useMemberMutation<{ role: 'director' | 'manager'; granted: boolean }, unknown>((api, base, input) =>
    api(`${base}/members/${p.userId}/roles`, { method: 'POST', body: { ...input, note } }),
  );
  const member = members.data?.find((x) => x.userId === p.userId);
  const roles = member?.roles ?? [];
  const toggle = (role: 'director' | 'manager', granted: boolean) => {
    if (window.confirm(m.confirmRole(p.party.displayName, t.roles[role], granted))) change.mutate({ role, granted }, { onSuccess: () => setNote('') });
  };
  const mine = history.data?.filter((h) => h.userId === p.userId) ?? [];

  return (
    <section className="panel">
      <h2>{m.access}</h2>
      <AccessCell party={p.party} status={access.data?.find((a) => a.partyId === p.party.id)} />
      {p.userId ? (
        <>
          <p className="small">
            {m.roles} : <strong>{roles.length ? roles.map((r) => t.roles[r]).join(', ') : '—'}</strong>
          </p>
          {s.can('members.roles') && (
            <div className="form">
              <Field label={m.roleNote} hint={t.common.optional}>
                <input value={note} onChange={(e) => setNote(e.target.value)} />
              </Field>
              <div className="form-actions">
                {roles.includes('director') ? (
                  <button type="button" className="button button-small" disabled={change.isPending} onClick={() => toggle('director', false)}>
                    {m.removeDirector}
                  </button>
                ) : (
                  <button type="button" className="button button-small button-primary" disabled={change.isPending} onClick={() => toggle('director', true)}>
                    {m.makeDirector}
                  </button>
                )}
                {roles.includes('manager') ? (
                  <button type="button" className="button button-small" disabled={change.isPending} onClick={() => toggle('manager', false)}>
                    {m.removeManager}
                  </button>
                ) : (
                  <button type="button" className="button button-small" disabled={change.isPending} onClick={() => toggle('manager', true)}>
                    {m.makeManager}
                  </button>
                )}
              </div>
              {change.isError && <Alert>{errorMessage(change.error, t)}</Alert>}
            </div>
          )}
          {roles.includes('director') && <OfficerTerms userId={p.userId} name={p.party.displayName} />}
          {mine.length > 0 && (
            <>
              <h3>{m.history}</h3>
              <ul className="compact-list">
                {mine.map((h) => (
                  <li key={h.id}>
                    <span>
                      {t.roles[h.role]} — {h.granted ? m.granted : m.removed}
                      {h.note && <span className="muted"> · {h.note}</span>}
                    </span>
                    <span className="muted small">{[date(h.changedAt.slice(0, 10)), h.changedBy && m.by(h.changedBy)].filter(Boolean).join(' ')}</span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </>
      ) : (
        <p className="muted small">
          {m.noAccount} {s.can('members.roles') && m.inviteFirst}
        </p>
      )}
    </section>
  );
}

/** A write about members; refreshes parties, access, members and ownership. */
function useMemberMutation<Input, Output>(request: (api: ReturnType<typeof useApi>, base: string, input: Input) => Promise<Output>) {
  const api = useApi();
  const s = useSyndicate();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: Input) => request(api, s.api, input),
    onSuccess: () => Promise.all(['parties', 'access', 'members', 'ownership', 'me'].map((key) => queryClient.invalidateQueries({ queryKey: key === 'me' ? ['me'] : [s.tenantId, key] }))),
  });
}
